import { css } from '@emotion/react';

import { scanValuePresenter } from '@kaiizen/kai-presenters';
import {
  getScanValueHasOverride,
  getUpdatedScanValue,
  NonNullableScanValueType,
  ScanTemplateValue,
  ScanValue,
} from '@kaiizen/kai-scan-template-system';

import { styleguideColors as COLORS } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { TOOLTIP_SECONDARY_TEXT, useTooltip } from '@/Client/Widgets';
import { styleguideFontFamily as FONT } from '@kaiizen/kai-shiki/core';

/** The color an overridden field wears: its frame, and the default caption beneath it. */
export const SCAN_VALUE_OVERRIDE_COLOR = COLORS.error;

/**
 * Marks the element (a line item row) whose hover turns its fields' default captions into Reset.
 * Without one, hovering or focusing the caption itself does.
 */
export const SCAN_VALUE_OVERRIDE_HOST_ATTRIBUTE = 'data-scan-value-override-host';
const OVERRIDE_HOST = `[${SCAN_VALUE_OVERRIDE_HOST_ATTRIBUTE}]`;

/**
 * Whether a scan value's override is worth presenting: the user has overridden a real default.
 * A zero default is a stand-in for "needs a value", not a real default, so filling it in is not
 * presented as an override: no red frame, no default caption.
 */
export const getScanValueOverridesRealDefault = (
  value: ScanValue | undefined | null,
): value is ScanValue & { setValue: NonNullableScanValueType; calculatedValue: NonNullableScanValueType } =>
  getScanValueHasOverride(value) && value.calculatedValue !== 0;

/** A small mono line in the override color, flush right under its field. */
const DEFAULT_CAPTION_STYLE = css`
  label: DEFAULT_CAPTION_STYLE;
  display: block;
  margin-top: 5px;
  margin-left: auto;
  padding: 0;
  border: 0;
  background: transparent;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(8.5)};
  letter-spacing: 0.04em;
  line-height: 1.2;
  color: ${SCAN_VALUE_OVERRIDE_COLOR};
  text-align: right;
  white-space: nowrap;
`;

/** In a line item row the caption hangs just under its field or photo, out of flow, so the row's contents stay aligned. */
const DEFAULT_CAPTION_HANGING_STYLE = css`
  label: DEFAULT_CAPTION_HANGING_STYLE;
  position: absolute;
  top: calc(100% + 5px);
  margin-top: 0;
`;

const DEFAULT_CAPTION_HANGING_RIGHT_STYLE = css`
  label: DEFAULT_CAPTION_HANGING_RIGHT_STYLE;
  right: 0;
`;

const DEFAULT_CAPTION_HANGING_LEFT_STYLE = css`
  label: DEFAULT_CAPTION_HANGING_LEFT_STYLE;
  left: 0;
  text-align: left;
`;

/** The caption as the reset control: at rest it names the default; hovering the row (or the caption) turns it into Reset. */
const DEFAULT_CAPTION_RESET_BUTTON_STYLE = css`
  label: DEFAULT_CAPTION_RESET_BUTTON_STYLE;
  cursor: pointer;

  & [data-caption-reset] {
    display: none;
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  ${OVERRIDE_HOST}:hover & [data-caption-default],
  &:hover [data-caption-default],
  &:focus-visible [data-caption-default] {
    display: none;
  }

  ${OVERRIDE_HOST}:hover & [data-caption-reset],
  &:hover [data-caption-reset],
  &:focus-visible [data-caption-reset] {
    display: inline;
  }
`;

/**
 * The caption under an override: "Default $0.50", in the override color, naming what was written
 * over. On hover it reads Reset, and its tooltip spells out the change and that a click resets it.
 * Without `onReset` it is a plain caption.
 */
export const FocusedDefaultCaption = ({
  defaultPresented,
  change,
  tooltipTitle,
  onReset,
  hangs,
}: {
  /** The default, presented: "$0.50"; without it the caption reads just "Default", where there's no room to name it. */
  defaultPresented?: string;
  /** The change, spelled out for the tooltip: "Changed to $0.75 from its default of $0.50." */
  change: string;
  /** What was overridden ("Price per Unit"), heading the tooltip as its override ("Price per Unit Override"). */
  tooltipTitle: string;
  onReset?: () => void;
  /** Hang the caption under its field or photo, flush to that side, rather than in flow. */
  hangs?: 'left' | 'right';
}) => {
  const captionTooltip = useTooltip(
    <>
      {change}
      {onReset && <p css={TOOLTIP_SECONDARY_TEXT}>{'Click to reset it to the default.'}</p>}
    </>,
    { title: `${tooltipTitle} Override` },
  );
  const caption = defaultPresented ? `Default ${defaultPresented}` : 'Default';
  const captionStyle = [
    DEFAULT_CAPTION_STYLE,
    hangs && DEFAULT_CAPTION_HANGING_STYLE,
    hangs === 'right' && DEFAULT_CAPTION_HANGING_RIGHT_STYLE,
    hangs === 'left' && DEFAULT_CAPTION_HANGING_LEFT_STYLE,
  ];

  if (!onReset) {
    return (
      <div css={captionStyle} data-testid='scan-value-default-caption' {...captionTooltip.anchorProps}>
        {caption}
        {captionTooltip.tooltip}
      </div>
    );
  }

  return (
    <button
      type='button'
      css={[captionStyle, DEFAULT_CAPTION_RESET_BUTTON_STYLE]}
      aria-label={`Reset to ${caption.toLowerCase()}`}
      onClick={event => {
        // the caption can sit inside a clickable area, such as the product cell that opens the product
        event.stopPropagation();
        onReset();
      }}
      data-testid='scan-value-default-caption'
      {...captionTooltip.anchorProps}
    >
      <span data-caption-default>{caption}</span>
      <span data-caption-reset>{'Reset'}</span>
      {captionTooltip.tooltip}
    </button>
  );
};

/**
 * The caption under an overridden scan value (see `FocusedDefaultCaption`), showing the calculated
 * value the user wrote over; clicking clears the override so the value returns to its default.
 * Renders nothing unless the value overrides a real default (see `getScanValueOverridesRealDefault`).
 */
export const FocusedScanValueDefaultCaption = ({
  valueKey,
  value,
  templateValue,
  updateValue,
  underTaskCardField = false,
  tooltipTitle,
  disabled = false,
}: {
  valueKey: string;
  value: ScanValue | undefined;
  templateValue: ScanTemplateValue;
  updateValue: (key: string, newValue: ScanValue) => void;
  /** The field is a line item row's (taskCardField): hang the caption under it rather than in flow. */
  underTaskCardField?: boolean;
  /** The value's name ("Price per Unit"), heading the caption's tooltip as its override ("Price per Unit Override"). */
  tooltipTitle: string;
  disabled?: boolean;
}) => {
  if (!getScanValueOverridesRealDefault(value)) return null;
  return (
    <FocusedDefaultCaption
      defaultPresented={scanValuePresenter(value.calculatedValue, templateValue)}
      change={`Changed to ${scanValuePresenter(value.setValue, templateValue)} from its default of ${
        scanValuePresenter(value.calculatedValue, templateValue)
      }.`}
      tooltipTitle={tooltipTitle}
      onReset={disabled ? undefined : () => updateValue(valueKey, getUpdatedScanValue(templateValue, value, undefined))}
      hangs={underTaskCardField ? 'right' : undefined}
    />
  );
};
